import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getErrorMessage, lessonService } from '../services/api';
import type { Difficulty, LessonSummary, LessonType, Readiness, User } from '../types/api';
import LessonCard from './LessonCard';
import { useToast } from './Toast';
import { useConfirm } from './ConfirmDialog';
import { BookOpen, ExternalLink, Plus, Search } from 'lucide-react';
import { EmptyState, LoadingState, Modal, PageHeader, Spinner } from './ui';

interface LessonFormData {
    title: string;
    description: string;
    world_file: string;
    editable_file_path: string;
    lesson_type: LessonType;
    tags: string;
    // '' until the teacher picks one: the form requires a choice.
    difficulty: Difficulty | '';
    readiness: Readiness;
    credit_id: string;
}

const EMPTY_FORM: LessonFormData = {
    title: '',
    description: '',
    world_file: '',
    editable_file_path: '',
    lesson_type: 'custom',
    tags: '',
    difficulty: '',
    readiness: 'stable',
    credit_id: '',
};

const NEW_LESSON_STARTER_CODE = '# Add your robot controller code here';

// Teachers manage their own custom lessons; admins manage everything (sync included).
const isLessonManageable = (lesson: LessonSummary, user: User | null) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return user.role === 'teacher' && lesson.lesson_type === 'custom' && lesson.created_by === user.id;
};

// The form's fields as the API wants them: tags as a list, and no credit as null (an empty string is not
// a credit id the backend can resolve).
const toLessonPayload = (data: LessonFormData) => ({
    title: data.title,
    description: data.description,
    world_file: data.world_file.trim(),
    editable_file_path: data.editable_file_path.trim(),
    lesson_type: data.lesson_type,
    tags: data.tags ? data.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
    difficulty: data.difficulty || undefined,
    readiness: data.readiness,
    credit_id: data.credit_id.trim() || null,
});

const inputClasses = 'input';
const labelClasses = 'label';

const LessonFormFields: React.FC<{
    data: LessonFormData;
    onChange: (data: LessonFormData) => void;
    isAdmin: boolean;
    showPlaceholders: boolean;
}> = ({ data, onChange, isAdmin, showPlaceholders }) => (
    <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <label className={labelClasses} htmlFor="lesson-title">Lesson Title *</label>
                <input
                    type="text"
                    id="lesson-title"
                    value={data.title}
                    onChange={(e) => onChange({ ...data, title: e.target.value })}
                    className={inputClasses}
                    placeholder={showPlaceholders ? 'e.g., Line Following Challenge' : undefined}
                />
            </div>
            <div>
                <label className={labelClasses} htmlFor="lesson-lesson-type">Lesson Type *</label>
                <select
                    id="lesson-lesson-type"
                    value={data.lesson_type}
                    onChange={(e) => onChange({ ...data, lesson_type: e.target.value as LessonType })}
                    disabled={!isAdmin}
                    className={inputClasses}
                >
                    <option value="custom">Custom (Only for you)</option>
                    <option value="official">Official (For all teachers)</option>
                </select>
            </div>
        </div>
        <div>
            <label className={labelClasses} htmlFor="lesson-description">Description *</label>
            <textarea
                id="lesson-description"
                value={data.description}
                onChange={(e) => onChange({ ...data, description: e.target.value })}
                className={inputClasses}
                rows={2}
                placeholder={showPlaceholders ? 'Brief description of the lesson' : undefined}
            />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <label className={labelClasses} htmlFor="lesson-difficulty">Difficulty *</label>
                <select
                    id="lesson-difficulty"
                    value={data.difficulty}
                    onChange={(e) => onChange({ ...data, difficulty: e.target.value as Difficulty | '' })}
                    className={inputClasses}
                    required
                >
                    <option value="" disabled>Choose a level</option>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                </select>
            </div>
            {/* LES-18: shown as a badge on the lesson's cards and page (none for stable). */}
            <div>
                <label className={labelClasses} htmlFor="lesson-readiness">Readiness</label>
                <select
                    id="lesson-readiness"
                    value={data.readiness}
                    onChange={(e) => onChange({ ...data, readiness: e.target.value as Readiness })}
                    className={inputClasses}
                >
                    <option value="preview">Preview: early, may still change</option>
                    <option value="beta">Beta: tested, being polished</option>
                    <option value="stable">Stable: no badge</option>
                </select>
                {data.lesson_type === 'official' && (
                    <p className="hint">Official lessons take theirs from the official lesson list (init_db.py): its next update sets it again.</p>
                )}
            </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <label className={labelClasses} htmlFor="lesson-tags">Tags</label>
                <input
                    type="text"
                    id="lesson-tags"
                    value={data.tags}
                    onChange={(e) => onChange({ ...data, tags: e.target.value })}
                    className={inputClasses}
                    placeholder="e.g. navigation, sensors, beginner"
                />
                <p className="hint">Comma-separated list of tags</p>
            </div>
            <div>
                <label className={labelClasses} htmlFor="lesson-credit-id">Credit ID</label>
                <input
                    type="text"
                    id="lesson-credit-id"
                    value={data.credit_id}
                    onChange={(e) => onChange({ ...data, credit_id: e.target.value })}
                    className={inputClasses}
                    placeholder="e.g. darko-lukic"
                />
                <p className="hint">Optional: the id of an existing credit entry</p>
            </div>
        </div>
        <div>
            <label className={labelClasses} htmlFor="lesson-world-file">World File URL *</label>
            <input
                type="text"
                id="lesson-world-file"
                value={data.world_file}
                onChange={(e) => onChange({ ...data, world_file: e.target.value })}
                className={`${inputClasses} font-mono text-xs`}
                placeholder={showPlaceholders ? 'https://github.com/username/repo/blob/master/worlds/example.wbt' : undefined}
            />
        </div>
        <div>
            <label className={labelClasses} htmlFor="lesson-editable-file-path">Editable File Path *</label>
            <input
                type="text"
                id="lesson-editable-file-path"
                value={data.editable_file_path}
                onChange={(e) => onChange({ ...data, editable_file_path: e.target.value })}
                className={`${inputClasses} font-mono text-xs`}
                placeholder={showPlaceholders ? 'controllers/my_controller/my_controller.py' : undefined}
            />
            <p className="hint">
                Relative path to the controller file students will edit
            </p>
        </div>
    </div>
);

const ContentLibrary: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const toast = useToast();
    const confirm = useConfirm();
    const [lessons, setLessons] = useState<LessonSummary[]>([]);
    const [saving, setSaving] = useState(false);
    const [lessonsLoading, setLessonsLoading] = useState(false);
    const [syncing, setSyncing] = useState<number | null>(null);
    const [openingEditFor, setOpeningEditFor] = useState<number | null>(null);
    const [showCreateLesson, setShowCreateLesson] = useState(false);
    const [editingLessonId, setEditingLessonId] = useState<number | null>(null);
    const [newLesson, setNewLesson] = useState<LessonFormData>(EMPTY_FORM);
    const [editLessonData, setEditLessonData] = useState<LessonFormData>(EMPTY_FORM);
    const [search, setSearch] = useState('');
    const isAdmin = user?.role === 'admin';

    const loadLessons = useCallback(async () => {
        setLessonsLoading(true);
        try {
            setLessons(await lessonService.list());
        } catch (error) {
            console.error('Error loading lessons:', error);
            toast.error(`Lessons could not be loaded: ${getErrorMessage(error)}`);
        } finally {
            setLessonsLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        loadLessons();
    }, [loadLessons]);

    const syncLesson = async (lessonId: number) => {
        // No confirmation: the repository is the lesson's source of truth, and theory and starter code can only
        // come from it.
        setSyncing(lessonId);
        try {
            await lessonService.syncFromGitHub(lessonId);
            toast.success('Lesson synced from GitHub');
            loadLessons();
        } catch (error) {
            console.error('Sync error details:', error);
            toast.error(`Sync failed: ${getErrorMessage(error, 'Failed to sync lesson. Please try again.')}`);
        } finally {
            setSyncing(null);
        }
    };

    const createLesson = async () => {
        if (!newLesson.difficulty) {
            toast.error('Choose the lesson\'s difficulty.');
            return;
        }
        setSaving(true);
        try {
            await lessonService.create({
                ...toLessonPayload(newLesson),
                starter_code: NEW_LESSON_STARTER_CODE,
            });
            setShowCreateLesson(false);
            setNewLesson(EMPTY_FORM);
            loadLessons();
            toast.success('Lesson created. Use "Sync GitHub" to import its README and controller.');
        } catch (error) {
            console.error('Error creating lesson:', error);
            toast.error(`Failed to create lesson: ${getErrorMessage(error, 'Unknown error')}`);
        } finally {
            setSaving(false);
        }
    };

    const deleteLesson = async (lessonId: number, title: string) => {
        const confirmed = await confirm({
            title: `Delete "${title}"?`,
            message: 'The lesson disappears from the library and from every class that uses it.',
            confirmLabel: 'Delete lesson',
            danger: true,
        });
        if (!confirmed) {
            return;
        }

        try {
            setLessonsLoading(true);
            await lessonService.remove(lessonId);
            toast.success('Lesson deleted');
            loadLessons();
        } catch (error) {
            console.error('Error deleting lesson:', error);
            toast.error(`Failed to delete lesson: ${getErrorMessage(error)}`);
            setLessonsLoading(false);
        }
    };

    const openEditLessonModal = async (lessonId: number) => {
        // The list holds summaries; the form starts from the full lesson so nothing it shows is a guess.
        setOpeningEditFor(lessonId);
        try {
            const lesson = await lessonService.get(lessonId);
            setEditLessonData({
                title: lesson.title,
                description: lesson.description || '',
                world_file: lesson.world_file,
                editable_file_path: lesson.editable_file_path,
                lesson_type: lesson.lesson_type,
                tags: (lesson.tags || []).join(', '),
                difficulty: lesson.difficulty || '',
                readiness: lesson.readiness || 'stable',
                credit_id: lesson.credit_id || lesson.credit?.id || '',
            });
            setEditingLessonId(lessonId);
        } catch (error) {
            toast.error(`The lesson could not be opened: ${getErrorMessage(error)}`);
        } finally {
            setOpeningEditFor(null);
        }
    };

    const updateLesson = async () => {
        if (editingLessonId === null) return;
        if (!editLessonData.difficulty) {
            toast.error('Choose the lesson\'s difficulty.');
            return;
        }

        try {
            setSaving(true);
            // Only the form's fields are sent (a PATCH): theory and starter code stay as synced.
            await lessonService.update(editingLessonId, toLessonPayload(editLessonData));
            setEditingLessonId(null);
            toast.success('Lesson saved');
            loadLessons();
        } catch (error) {
            console.error('Error updating lesson:', error);
            toast.error(`Failed to update lesson: ${getErrorMessage(error)}`);
        } finally {
            setSaving(false);
        }
    };

    // A search over title, description and tags: the library grows with every teacher's custom lessons.
    const query = search.trim().toLowerCase();
    const matches = (lesson: LessonSummary) => !query
        || [lesson.title, lesson.description || '', ...(lesson.tags || [])].some((text) => text.toLowerCase().includes(query));
    const customLessons = lessons.filter(l => l.lesson_type === 'custom' && matches(l));
    const officialLessons = lessons.filter(l => l.lesson_type === 'official' && matches(l));

    const renderCard = (lesson: LessonSummary) => (
        <LessonCard
            key={lesson.id}
            lesson={lesson}
            canManage={isLessonManageable(lesson, user)}
            syncing={syncing === lesson.id}
            onPreview={() => navigate(`/lesson/${lesson.id}`)}
            onEdit={() => openEditLessonModal(lesson.id)}
            onDelete={() => deleteLesson(lesson.id, lesson.title)}
            onSync={() => syncLesson(lesson.id)}
        />
    );

    const isFormComplete = (data: LessonFormData) => Boolean(data.title && data.world_file && data.editable_file_path);

    return (
        <div>
            <PageHeader
                title="Content Library"
                subtitle="Manage and organize your robotics lessons"
                actions={
                    <button onClick={() => setShowCreateLesson(true)} className="btn btn-primary btn-lg">
                        <Plus />
                        <span>Create New Lesson</span>
                    </button>
                }
            />

            {lessons.length > 0 && (
                <div className="relative mb-6 max-w-md">
                    <label htmlFor="library-search" className="sr-only">Search lessons</label>
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                        id="library-search"
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by title, description or tag"
                        className="input pl-9"
                    />
                </div>
            )}

            {openingEditFor !== null && (
                <div className="mb-4 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400" role="status">
                    <Spinner className="w-4 h-4" /> Opening the lesson...
                </div>
            )}

            {lessonsLoading ? (
                <LoadingState label="Loading lessons..." />
            ) : lessons.length === 0 ? (
                <EmptyState icon={<BookOpen />} title="No lessons available.">
                    Create your first custom lesson!
                </EmptyState>
            ) : customLessons.length === 0 && officialLessons.length === 0 ? (
                <EmptyState icon={<Search />} title="No lessons match your search." action={
                    <button onClick={() => setSearch('')} className="btn btn-secondary">Clear Search</button>
                } />
            ) : (
                <div className="space-y-10">
                    {customLessons.length > 0 && (
                        <section className="min-w-0">
                            <h3 className="eyebrow mb-3 px-1">Your Custom Lessons <span className="text-gray-400 dark:text-gray-500">· {customLessons.length}</span></h3>
                            <div className="space-y-3">{customLessons.map(renderCard)}</div>
                        </section>
                    )}

                    {officialLessons.length > 0 && (
                        <section className="min-w-0">
                            <h3 className="eyebrow mb-3 px-1">Official Lessons <span className="text-gray-400 dark:text-gray-500">· {officialLessons.length}</span></h3>
                            <div className="space-y-3">{officialLessons.map(renderCard)}</div>
                        </section>
                    )}
                </div>
            )}

            {/* Create Lesson Modal */}
            {showCreateLesson && (
                <Modal
                    title="Create New Lesson"
                    size="lg"
                    onClose={() => setShowCreateLesson(false)}
                    description={
                        <a
                            href="https://github.com/SpesRobotics/webots-academy-example"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="link inline-flex items-center gap-1.5"
                        >
                            Instructions for adding a custom lesson
                            <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                    }
                    footer={
                        <>
                            <button onClick={() => setShowCreateLesson(false)} className="btn btn-secondary">
                                Cancel
                            </button>
                            <button onClick={createLesson} disabled={!isFormComplete(newLesson) || saving} className="btn btn-primary">
                                {saving ? 'Creating...' : 'Create Lesson'}
                            </button>
                        </>
                    }
                >
                    <LessonFormFields data={newLesson} onChange={setNewLesson} isAdmin={isAdmin} showPlaceholders />
                </Modal>
            )}

            {/* Edit Lesson Modal */}
            {editingLessonId !== null && (
                <Modal
                    title="Edit Lesson"
                    size="lg"
                    onClose={() => setEditingLessonId(null)}
                    footer={
                        <>
                            <button onClick={() => setEditingLessonId(null)} className="btn btn-secondary">
                                Cancel
                            </button>
                            <button onClick={updateLesson} disabled={!isFormComplete(editLessonData) || saving} className="btn btn-primary">
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </>
                    }
                >
                    <LessonFormFields data={editLessonData} onChange={setEditLessonData} isAdmin={isAdmin} showPlaceholders={false} />
                </Modal>
            )}
        </div>
    );
};

export default ContentLibrary;
